import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Grid, Card, CardHeader, CardContent, Typography, Box, Button
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import AddIcon from '@mui/icons-material/Add';
import { useActionSettings } from '../../hooks/useActionSettings';
import ActionSettingsLayout from '../ActionSettingsHeader';
import { useFolderPicker } from '../useFolderPicker';
import { useFolderRestrictions } from '../useFolderRestrictions';
import TagEditor from '../Widgets/TagEditor';

const ActionsConvertDocxToPdf = ({ caseid, tags, settingId, onSaveStatusChange, initialScope }) => {
  const { t } = useTranslation();
  const [sourceDisplayPath, setSourceDisplayPath] = useState('');
  const [targetPath, setTargetPath] = useState('');
  const [sourceBrowseDisabled, setSourceBrowseDisabled] = useState(false);
  const [targetBrowseDisabled, setTargetBrowseDisabled] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const targetRef = useRef(null);
  const loadedSourcePathRef = useRef('');
  const loadedTargetPathRef = useRef('');

  const { restrictToUserFolder, allowedRoots, displayRoot } = useFolderRestrictions(caseid);

  const normalizePath = useCallback((p) => (p || '').replace(/\\/g, '/'), []);

  const getDisplayPath = useCallback((absPath) => {
    if (!absPath) return '';
    if (restrictToUserFolder && allowedRoots && allowedRoots.length > 0) {
      const primaryRoot = normalizePath(allowedRoots[0]);
      const normalized = normalizePath(absPath);
      if (normalized === primaryRoot) return '/';
      if (normalized.startsWith(primaryRoot + '/')) {
        const rel = normalized.slice(primaryRoot.length);
        return rel ? `/${rel.replace(/^[\\/]+/, '')}` : '/';
      }
    }
    return absPath;
  }, [restrictToUserFolder, allowedRoots, normalizePath]);

  useEffect(() => {
    if (loadedSourcePathRef.current) {
      setSourceDisplayPath(getDisplayPath(loadedSourcePathRef.current));
    }
    if (loadedTargetPathRef.current) {
      setTargetPath(getDisplayPath(loadedTargetPathRef.current));
    }
  }, [getDisplayPath]);

  const compareSettings = useCallback((initial) => {
    const initialTargetDisplay = initial.targetPath ? getDisplayPath(initial.targetPath) : '';
    const initialSourceDisplay = initial.sourceFilePath ? getDisplayPath(initial.sourceFilePath) : '';
    return (
      initialSourceDisplay === sourceDisplayPath &&
      initialTargetDisplay === targetPath
    );
  }, [getDisplayPath, sourceDisplayPath, targetPath]);

  const buildSaveData = useCallback(() => ({
    sourceFilePath: sourceDisplayPath,
    targetPath: targetPath
  }), [sourceDisplayPath, targetPath]);

  // Use unified action settings hook
  const {
    isEnabled,
    setIsEnabled,
    isSaveDisabled,
    conditions,
    forceRunLoading,
    forceRunResult,
    popUpOpen,
    forceRunWarningOpen,
    forceRunWarningCount,
    handleConditionsChange,
    handleSave: baseHandleSave,
    handleForceRun,
    handleForceRunConfirm,
    handleForceRunCancel,
    handleCloseStatusWindow,
    loadedCustomData,
  } = useActionSettings({
    settingId,
    caseid,
    actionType: 'convertdocxtopdf',
    onSaveStatusChange,
    customFields: {
      sourceFilePath: '',
      targetPath: '/{filename}.pdf'
    },
    customComparison: compareSettings,
    customSaveData: buildSaveData,
    enableConditions: true,
    initialScope
  });

  useEffect(() => {
    if (!loadedCustomData || Object.keys(loadedCustomData).length === 0) {
      return;
    }

    if (loadedCustomData.sourceFilePath !== undefined) {
      loadedSourcePathRef.current = loadedCustomData.sourceFilePath;
      setSourceDisplayPath(getDisplayPath(loadedSourcePathRef.current));
    }
    if (loadedCustomData.targetPath) {
      loadedTargetPathRef.current = loadedCustomData.targetPath;
      setTargetPath(getDisplayPath(loadedTargetPathRef.current));
    }
  }, [loadedCustomData, getDisplayPath]);

  const { handleBrowseDirectory: handleBrowseSourceDirectory, FolderBrowserComponent: SourceFolderBrowser } = useFolderPicker({
    restrictToUserFolder,
    allowedRoots,
    displayRoot,
    allowFileSelection: true,
    onFolderSelected: (folderPath) => {
      setSourceDisplayPath(getDisplayPath(folderPath));
    },
    onFolderBrowserClose: () => {
      setSourceBrowseDisabled(false);
    }
  });

  const { handleBrowseDirectory: handleBrowseTargetDirectory, FolderBrowserComponent: TargetFolderBrowser } = useFolderPicker({
    restrictToUserFolder,
    allowedRoots,
    displayRoot,
    allowFileSelection: false,
    onFolderSelected: (folderPath) => {
      const display = getDisplayPath(folderPath);
      const folderClean = display.replace(/[\\/]+$/, '');
      const separator = folderClean.includes('\\') ? '\\' : '/';
      setTargetPath(`${folderClean}${separator}{filename}.pdf`);
    },
    onFolderBrowserClose: () => {
      setTargetBrowseDisabled(false);
    }
  });

  // Available tags for insertion
  const availableTags = useMemo(() => {
    if (!tags || !Array.isArray(tags)) return [];
    
    return tags.map((tag) => ({
      key: tag.key || tag.value || tag.variable || tag.label,
      label: tag.label || tag.key || tag.value || tag.variable,
      value: tag.value || tag.variable || tag.key || tag.label,
      variable: tag.variable || tag.value || tag.key || tag.label,
      description: tag.description || '',
      type: tag.type || 'text',
      reply: tag.reply || '',
      tag_type: tag.tag_type || ''
    }));
  }, [tags]);

  const customDataTags = [
    { 
      key: 'filename', 
      label: 'Filename', 
      value: 'filename', 
      description: 'Original filename without extension' 
    },
    { 
      key: 'current_date', 
      label: 'Current Date', 
      value: 'current_date', 
      description: 'Current date in YYYY-MM-DD format' 
    },
    { 
      key: 'projectid', 
      label: 'Project ID', 
      value: 'projectid', 
      description: 'Current project identifier' 
    }
  ];

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleTagSelect = (value) => {
    targetRef.current?.insertTag(value);
    handleMenuClose();
  };

  return (
    <ActionSettingsLayout
      isEnabled={isEnabled}
      onEnabledChange={setIsEnabled}
      title={t('workflow.convertdocxtopdf', 'Convert DOCX to PDF')}
      isSaveDisabled={isSaveDisabled}
      onSave={() => baseHandleSave()}
      onForceRun={handleForceRun}
      forceRunLoading={forceRunLoading}
      conditions={conditions}
      onConditionsChange={handleConditionsChange}
      tags={availableTags}
      enableConditions={true}
      popUpOpen={popUpOpen}
      forceRunResult={forceRunResult}
      onCloseStatusWindow={handleCloseStatusWindow}
      forceRunWarningOpen={forceRunWarningOpen}
      forceRunWarningCount={forceRunWarningCount}
      onForceRunConfirm={handleForceRunConfirm}
      onForceRunCancel={handleForceRunCancel}
      tagsDropdownProps={{
        tags: availableTags,
        handleTagSelect: handleTagSelect,
        anchorEl: anchorEl,
        setAnchorEl: handleMenuClose,
        menuType: 'target',
        additionalTags: customDataTags
      }}
    >
      <Grid container spacing={3}>
        {/* Source File Path */}
        <Grid item xs={12}>
          <Card sx={{
            borderRadius: '12px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
            border: '1px solid #5757b010',
            overflow: 'hidden',
          }}>
            <CardHeader
              title={
                <Typography sx={{
                  fontWeight: 700,
                  color: '#444',
                  fontSize: { xs: '1rem', sm: '1.1rem' },
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  <Box component="span" sx={{
                    bgcolor: '#5757b0',
                    color: 'white',
                    borderRadius: '6px',
                    px: 1.5,
                    py: 0.7,
                    mr: { xs: 1, sm: 2 },
                    fontSize: '0.85rem',
                    letterSpacing: '0.5px',
                    fontWeight: 600,
                    boxShadow: '0 3px 6px rgba(87, 87, 176, 0.2)',
                  }}>
                    SOURCE
                  </Box>
                  {t('workflow.source_file_path', 'Source DOCX File')}
                </Typography>
              }
              sx={{
                borderBottom: '1px solid #f0f0f8',
                backgroundColor: '#fafbff',
                p: { xs: 1.5, sm: 2.5 },
              }}
            />
            <CardContent sx={{ pt: 2.5, pb: 2.5 }}>
              <Box sx={{
                mb: 1.5,
                border: '1px solid #d0d0e8',
                borderRadius: '8px',
                overflow: 'hidden',
                backgroundColor: '#fff',
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: '#5757b0',
                },
                '&:focus-within': {
                  borderColor: '#5757b0',
                  boxShadow: '0 0 0 3px rgba(87, 87, 176, 0.1)',
                },
              }}>
                 <TagEditor
                    caseid={caseid}
                    value={sourceDisplayPath}
                    onChange={setSourceDisplayPath}
                    placeholder="/path/to/source/document.docx"
                    singleLine
                    tags={tags}
                  />
              </Box>

              <Box sx={{
                display: 'flex',
                justifyContent: 'flex-start',
                alignItems: 'center',
                gap: 1,
                mb: 1,
              }}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    setSourceBrowseDisabled(true);
                    handleBrowseSourceDirectory();
                  }}
                  disabled={sourceBrowseDisabled}
                  startIcon={<FolderOpenIcon />}
                  sx={{ textTransform: 'none' }}
                >
                  {t('common.browse', 'Browse')}
                </Button>
              </Box>
              <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                {t('workflow.source_docx_help', 'Path to the DOCX file that needs to be converted to PDF')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Target Path */}
        <Grid item xs={12}>
          <Card sx={{
            borderRadius: '12px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
            border: '1px solid #5757b010',
            overflow: 'hidden',
          }}>
            <CardHeader
              title={
                <Typography sx={{
                  fontWeight: 700,
                  color: '#444',
                  fontSize: { xs: '1rem', sm: '1.1rem' },
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  <Box component="span" sx={{
                    bgcolor: '#5757b0',
                    color: 'white',
                    borderRadius: '6px',
                    px: 1.5,
                    py: 0.7,
                    mr: { xs: 1, sm: 2 },
                    fontSize: '0.85rem',
                    letterSpacing: '0.5px',
                    fontWeight: 600,
                    boxShadow: '0 3px 6px rgba(87, 87, 176, 0.2)',
                  }}>
                    <PictureAsPdfIcon sx={{ mr: 0.5, fontSize: '0.85rem' }} />
                    OUTPUT
                  </Box>
                  {t('workflow.pdf_output_path', 'PDF Output Path')}
                </Typography>
              }
              sx={{
                borderBottom: '1px solid #f0f0f8',
                backgroundColor: '#fafbff',
                p: { xs: 1.5, sm: 2.5 },
              }}
            />              
            <CardContent sx={{ pt: 2.5, pb: 2.5 }}>
              <TagEditor
                    caseid={caseid}
                ref={targetRef}
                value={targetPath}
                onChange={setTargetPath}
                placeholder={t('workflow.pdf_path_placeholder', 'Enter output path like /output/{filename}.pdf')}
                singleLine
                onTagClick={handleMenuOpen}
                tags={tags}
              />

              <Box sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1,
                alignItems: 'center',
                mt: 1.5,
              }}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    setTargetBrowseDisabled(true);
                    handleBrowseTargetDirectory();
                  }}
                  disabled={targetBrowseDisabled}
                  startIcon={<FolderOpenIcon />}
                  sx={{ textTransform: 'none' }}
                >
                  {t('common.browse', 'Browse')}
                </Button>
              </Box>
              <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                {t('workflow.pdf_output_help', 'Output path for the converted PDF file. Use tags like {filename} or {current_date} for dynamic naming.')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {SourceFolderBrowser}
      {TargetFolderBrowser}
    </ActionSettingsLayout>
  );
};

export default React.memo(ActionsConvertDocxToPdf);
